import React from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';
import { matchPath } from 'react-router';
import Menu, { setActiveItems } from '../../components/Menu';
import MenuItem from '../../components/MenuItem';
import ArrowIcon from '../../../../img/sprite/arrow-right.svg';
import { ORG_PATHS } from '../../constants';
import { injectParams } from '../../utils/pathHelpers';
import { getSelectedProfileBillingAccounts } from '../../selectors/OrgManagementSelectors';

const SharedIconStyles = css`
  text-decoration: none;
  display: inline-block;
  font-size: 13px;
  li  li & {
    display: none;
  }
`;

const ActiveIcon = styled.svg`
  ${SharedIconStyles}
  margin-left: 0.692em;
  margin-top: -0.308em;
  transform: rotate(90deg);
`;

const InactiveIcon = styled.svg`
  ${SharedIconStyles}
  margin-left: 0.462em;
  margin-top: -0.231em;
`;

const renderOrderHistoryItems = ({ organizationId, billingAccounts, NavItem, NavList }) => {
  if (billingAccounts.length === 0) {
    return (
      <NavItem
        title="Order History"
        href={injectParams(ORG_PATHS.ORDER_HISTORY_BASE, { organizationId })}
        data-qa-hook="orderHistory.link"
      />
    );
  } else if (billingAccounts.length === 1) {
    const account = billingAccounts[0];
    return (
      <NavItem
        title="Order History"
        href={injectParams(ORG_PATHS.ORDER_HISTORY, { organizationId, billingAccountId: account.id })}
        data-qa-hook="orderHistory.link"
      />
    );
  } else {
    const firstAccount = billingAccounts[0];
    return (
      <NavItem
        title="Order History"
        href={injectParams(ORG_PATHS.ORDER_HISTORY, { organizationId, billingAccountId: firstAccount.id })}
        data-qa-hook="orderHistory.link"
      >
        <NavList>
          {billingAccounts.map(account => (
            <NavItem
              key={account.id}
              title={`Acct ${account.accountNumber}`}
              href={injectParams(ORG_PATHS.ORDER_HISTORY, { organizationId, billingAccountId: account.id })}
              data-qa-hook={`orderHistory.link.${account.id}`}
            />
          ))}
        </NavList>
      </NavItem>
    );
  }
};

/* eslint-disable react/prop-types */
const renderBillingAccountItems = ({ organizationId, billingAccounts, NavItem, NavList, isMobile, location }) => {

  if (billingAccounts.length === 0) {
    return (
      <NavItem
        title="Account Activity"
        href={injectParams(ORG_PATHS.ACCOUNT_ACTIVITY_BASE, { organizationId })}
        data-qa-hook="accountActivity.link"
      />
    );
  } else if (billingAccounts.length === 1) {
    const account = billingAccounts[0];
    return (
      <NavItem
        title="Account Activity"
        href={injectParams(ORG_PATHS.ACCOUNT_ACTIVITY, { organizationId, billingAccountId: account.id })}
        data-qa-hook="accountActivity.link"
      />
    );
  } else {
    const firstAccount = billingAccounts[0];
    return (
        <NavItem
          title="Account Activity"
          href={injectParams(ORG_PATHS.ACCOUNT_ACTIVITY, { organizationId, billingAccountId: firstAccount.id })}
          data-qa-hook="accountActivity.link"
        >
          <NavList>
            {billingAccounts.map((account) => (
              <NavItem
                key={account.id}
                title={`Acct ${account.accountNumber}`}
                href={injectParams(ORG_PATHS.ACCOUNT_ACTIVITY, { organizationId, billingAccountId: account.id })}
                data-qa-hook={`accountActivity.link.${account.id}`}
              />
            ))}
          </NavList>
        </NavItem>
    );
  }
};

const renderAcctSettings = ({ organizationId, billingAccounts, NavItem, NavList, isMobile, location }) => {
  if (billingAccounts.length === 0) {
    return (
        <NavItem
          title="Account Settings"
          href={injectParams(ORG_PATHS.ACCOUNT_SETTINGS_BASE, { organizationId })}
          data-qa-hook="accountSettings.link"
        />
    );
  } else if (billingAccounts.length === 1) {
    const account = billingAccounts[0];
    return (
      <NavItem
        title="Account Settings"
        href={injectParams(ORG_PATHS.ACCOUNT_SETTINGS, { organizationId, billingAccountId: account.id })}
        data-qa-hook="accountSettings.link"
      />
    );
  } else {
    const firstAccount = billingAccounts[0];
    return (
      <NavItem
        title="Account Settings"
        href={injectParams(ORG_PATHS.ACCOUNT_SETTINGS, { organizationId, billingAccountId: firstAccount.id })}
        data-qa-hook="accountSettings.link"
      >
        <NavList>
          {billingAccounts.map((account) => (
            <NavItem
              key={account.id}
              title={`Acct ${account.accountNumber}`}
              href={injectParams(ORG_PATHS.ACCOUNT_SETTINGS, { organizationId, billingAccountId: account.id })}
              data-qa-hook={`accountSettings.link.${account.id}`}
            />
          ))}
        </NavList>
      </NavItem>
    );
  }
};

/* eslint-enable react/prop-types */

export const OrgNavMenu = (props) => {
  const { location, isOpen, navToggle, organizationId, billingAccounts, isMobile } = props;

  const styles = props.styles || {};
  const icons = props.icons || {
    activeIcon: <ActiveIcon><use xlinkHref={`#${ArrowIcon.id}`} /></ActiveIcon>,
    inactiveIcon: <InactiveIcon><use xlinkHref={`#${ArrowIcon.id}`} /></InactiveIcon>
  };
  const NavItem = (props) => <MenuItem location={location} icons={icons} styles={styles} navToggle={navToggle} isOpen={isOpen} {...props} />;
  const NavList = (props) => <Menu styles={styles} {...props} />;

  const OrderHistoryItems = renderOrderHistoryItems({ organizationId, billingAccounts, NavItem, NavList });
  const AccountActivityItems = renderBillingAccountItems({ organizationId, billingAccounts, NavItem, NavList, isMobile, location });
  const AccountSettingsItems = renderAcctSettings({ organizationId, billingAccounts, NavItem, NavList, isMobile, location });

  const menu = (
    <NavList isActive>
      <NavItem
        firstChild
        title="Profile"
        href={injectParams(ORG_PATHS.PROFILE, { organizationId })}
        data-qa-hook="profile.link"
      />
      {OrderHistoryItems}
      {AccountActivityItems}
      {AccountSettingsItems}
      <NavItem
        lastChild={!isMobile}
        title="Groups"
        href={injectParams(ORG_PATHS.GROUPS, { organizationId })}
        data-qa-hook="groups.link"
      />
    </NavList>
  );

  const _crumbs = [];
  const navMenu = setActiveItems(menu, props, 0, false, _crumbs);

  return navMenu;
};

const { object, func, bool, shape, node, string, array, arrayOf } = PropTypes;

OrgNavMenu.propTypes = {
  billingAccounts: arrayOf(shape({
    billingAccountId: string,
    organizationName: string
  })).isRequired,

  location: object.isRequired,
  navToggle: func,
  noRender: bool,
  isOpen: bool,
  isMobile: bool,

  // are used on mobile to create the breadcrumb menu (in setActiveItems)
  isBreadCrumb: bool,
  renderBreadCrumb: func,

  icons: shape({
    iconActive: node,
    iconInactive: node
  }),
  // Will warn sometimes until all are changed to Styled-Components: string VS array //
  styles: shape({
    ulCommon: oneOfType([array, string]),
    ulActive: oneOfType([array, string]),
    ulInactive: oneOfType([array, string]),
    liCommon: oneOfType([array, string]),
    liActive: oneOfType([array, string]),
    liInactive: oneOfType([array, string]),
    aCommon: oneOfType([array, string]),
    aActive: oneOfType([array, string]),
    aInactive: oneOfType([array, string]),
    firstChildStyles: object,
    lastChildStyles: object,
  })
};

function mapStateToProps(state) {
  const route = state.router.location.pathname;
  const orgRouteMatch = matchPath(route, { path: ORG_PATHS.BASE });
  const organizationId = orgRouteMatch?.params?.organizationId;
  const orgActiveBillingAccounts = getSelectedProfileBillingAccounts(state).filter(ba => ba.active && ba.userCanAccess) || [];

  return {
    organizationId,
    billingAccounts: orgActiveBillingAccounts,
  };
}

function mapDispatchToProps(dispatch) {
  return {
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OrgNavMenu);
